/**
 * VRPreview — live WebGL preview for the video-to-vr tool.
 *
 * Three render modes:
 *   'output' — WYSIWYG of the final file (SBS + parallax + barrel pre-distortion)
 *   'hmd'    — simulates what the file looks like through VR-headset lenses
 *              (two circular viewports with pincushion applied on top of barrel)
 *   'xr'     — (opt-in, via enterXR()) renders the same SBS texture into a
 *              real WebXR immersive-vr session, one eye per XRView
 *
 * All three share a single shader that samples the source <video> once per
 * fragment and transforms texture coordinates; mode/settings switch via uniforms.
 *
 *   const vp = new VRPreview(containerEl);
 *   vp.load(fileOrUrl);
 *   vp.setSettings({mode, parallax, headset, ipd, barrel, resolution, metadata});
 *   vp.setView('output' | 'hmd');
 *   vp.play(); vp.pause(); vp.seek(t);
 *   vp.enterXR();
 *   vp.destroy();
 *
 *   vp.video          // raw <video> element
 *   vp.isXRSupported  // Promise<bool>  — resolves once on construction
 *
 * Events dispatched on container:
 *   'vrpreview:ready'         detail.video, .w, .h, .duration
 *   'vrpreview:play' / 'pause'/ 'ended' / 'timeupdate'
 *   'vrpreview:xrstart' / 'xrend' / 'xrerror'
 */
(function(){
'use strict';

/* ═══════════════════════════════════════════════════════════════════════════
   Shader — single fragment program, mode switched via int uniform.
   ─────────────────────────────────────────────────────────────────────────
   uMode 0 = output SBS (no parallax, no barrel)       — set by JS as composite
   uMode 1 = output (SBS + parallax + barrel)
   uMode 2 = HMD view (output + lens pincushion, two circular viewports)
   uMode 3 = passthrough (source is already SBS; just copy)
   Keeping the mode split explicit in the shader avoids branchy uniforms
   in the hot loop per fragment — each mode is a flat branch taken once
   per primitive on modern GPUs.
   ═══════════════════════════════════════════════════════════════════════════ */
var VERT_SRC = [
    'attribute vec2 aPos;',
    'varying vec2 vUv;',
    'void main(){',
    '    vUv = vec2(aPos.x * 0.5 + 0.5, 1.0 - (aPos.y * 0.5 + 0.5));',
    '    gl_Position = vec4(aPos, 0.0, 1.0);',
    '}'
].join('\n');

var FRAG_SRC = [
    'precision highp float;',
    'uniform sampler2D uTex;',
    'uniform int   uMode;',              /* 0=sbs 1=parallax 2=passthrough */
    'uniform int   uView;',              /* 0=output 1=hmd */
    'uniform int   uSphere;',            /* 1 = curved 360 (equirect band) — overrides mode/view */
    'uniform float uSphereHalfW;',       /* half-width of the video band, fraction of equirect canvas */
    'uniform float uSphereHalfH;',       /* half-height of the video band, fraction of equirect canvas */
    'uniform float uParallaxFrac;',      /* horizontal shift as fraction of src width */
    'uniform vec2  uBarrelK;',           /* (k1, k2) — negative for barrel pre-distortion */
    'uniform vec2  uLensK;',             /* (L1, L2) — positive for pincushion lens */
    'uniform float uSrcAspect;',         /* source width/height */
    'uniform float uOutAspect;',         /* output canvas aspect — for letterbox in resolution modes */
    'uniform float uCanvasAspect;',      /* canvas w/h */
    'uniform float uPadScale;',          /* 1.0 = fill; <1.0 = letterbox */
    'uniform float uIpdFrac;',           /* center-to-center distance of eye viewports, as fraction of half-width */
    'uniform float uEyeRadius;',         /* eye viewport radius, fraction of half-width */
    'varying vec2 vUv;',

    /* Radial lens distortion — the ONE implementation, taken from vr-render.js
       (window.VRRender.GLSL_DISTORT) so the preview and the delivered file
       cannot describe the same knob differently again. Until 2026-08-19 this
       function normalised the radius per axis in UV space while FFmpeg's
       lenscorrection normalises by the frame's half-diagonal in PIXEL space:
       measured on ffmpeg itself, k1=-0.3 on an 800x400 frame moves a line at
       x=600 to x=613..615, and the old maths put it near 604 while bending the
       frame elliptically. Same setting, two pictures — the preview was the one
       lying, because the file is what the user keeps. */
    (window.VRRender ? window.VRRender.GLSL_DISTORT : ''),
    /* The barrel term addresses the whole SOURCE frame, so it carries the
       source aspect; the HMD pincushion addresses one eye's roughly square
       viewport and passes 1.0. Getting that argument wrong is how an
       isotropic lens turns elliptical. */
    'vec2 radialDistort(vec2 uv, vec2 k, float aspect){ return vrLensDistort(uv, k, aspect); }',

    /* One-eye sample: given a UV that addresses a single-eye viewport
       ([0,1]x[0,1] relative to that eye), return the SBS-output colour for
       that eye. `eye` is 0 for left, 1 for right. */
    'vec4 sampleEye(vec2 eyeUv, float eye){',
    /* Apply barrel pre-distortion if enabled — mirrors FFmpeg lenscorrection,
       applied to the SOURCE before splitting. */
    '    vec2 srcUv = radialDistort(eyeUv, uBarrelK, uSrcAspect);',
    '    if (uMode == 0) {',                 /* SBS — both eyes sample full source */
    '        return texture2D(uTex, srcUv);',
    '    } else if (uMode == 1) {',          /* Parallax — shift each eye */
    '        float p = uParallaxFrac;',
    /* Left eye crops from x=0..(1-p) and scales; Right eye crops from x=p..1.
       In UV space that means: L: u -> u*(1-p); R: u -> u*(1-p) + p.
       Our eyeUv is already [0..1] per eye, so map it into that range. */
    '        srcUv.x = mix(p * eye, (1.0 - p) + p * eye, eyeUv.x);',
    /* Re-apply barrel because we distorted before knowing parallax. Cheap. */
    '        return texture2D(uTex, srcUv);',
    '    } else {',                          /* Passthrough — source is already SBS */
    /* Sample left or right half of source per eye. */
    '        vec2 u = vec2(eyeUv.x * 0.5 + 0.5 * eye, eyeUv.y);',
    '        u = radialDistort(u, uBarrelK, uSrcAspect);',
    '        return texture2D(uTex, u);',
    '    }',
    '}',

    'void main(){',
    /* Sphere (curved 360) mode: the canvas IS the equirectangular frame (2:1).
       The flat video occupies a centered band; everything else is black. This
       is exactly what the FFmpeg scale+pad produces, so the preview is WYSIWYG
       of the equirect file. HMD/parallax/barrel do not apply here. */
    '    if (uSphere == 1) {',
    '        vec2 uv = vUv;',
    '        float x0 = 0.5 - uSphereHalfW, x1 = 0.5 + uSphereHalfW;',
    '        float y0 = 0.5 - uSphereHalfH, y1 = 0.5 + uSphereHalfH;',
    '        if (uv.x < x0 || uv.x > x1 || uv.y < y0 || uv.y > y1) {',
    '            gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);',
    '            return;',
    '        }',
    '        vec2 t = vec2((uv.x - x0) / (x1 - x0), (uv.y - y0) / (y1 - y0));',
    '        gl_FragColor = texture2D(uTex, t);',
    '        return;',
    '    }',
    '    if (uView == 0) {',
    /* Output view: canvas is split 50/50 by x. Apply optional letterbox
       via uPadScale by centering + padding the output rect. */
    '        vec2 uv = vUv;',
    '        vec2 pad = vec2((1.0 - uPadScale) * 0.5);',
    '        if (uv.x < pad.x || uv.x > 1.0 - pad.x || uv.y < pad.y || uv.y > 1.0 - pad.y) {',
    '            gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);',
    '            return;',
    '        }',
    '        vec2 inner = (uv - pad) / vec2(uPadScale);',
    '        float eye = inner.x < 0.5 ? 0.0 : 1.0;',
    '        vec2 eyeUv = vec2((inner.x - 0.5 * eye) * 2.0, inner.y);',
    '        gl_FragColor = sampleEye(eyeUv, eye);',
    '    } else {',
    /* HMD view: two circular viewports centered at offsets determined by IPD.
       Everything else is solid black (the cardboard case interior).
       We apply pincushion lens distortion to simulate what the eye perceives. */
    '        vec2 uv = vUv;',
    '        float eye = uv.x < 0.5 ? 0.0 : 1.0;',
    /* Map to per-eye local space centered on that eye. Each eye owns half the canvas. */
    '        vec2 local = vec2((uv.x - 0.5 * eye) * 2.0, uv.y);',  /* [0..1] x [0..1] */
    /* Correct for the canvas aspect — we want perfectly circular viewports.
       Each eye is a half-width aspect of 0.5*canvasAspect. */
    '        float halfAspect = uCanvasAspect * 0.5;',
    '        vec2 p = local - 0.5;',
    '        p.x *= halfAspect;',            /* scale to physical square */
    /* Shift center laterally by half-IPD (inward) — eyes are slightly outward of centres. */
    '        float ipdOffset = (uIpdFrac - 1.0) * 0.25;',  /* for uIpdFrac=1 → 0 offset */
    '        p.x += (eye == 0.0 ? -ipdOffset : ipdOffset);',
    '        float dist = length(p);',
    '        if (dist > uEyeRadius) {',
    '            gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);',
    '            return;',
    '        }',
    /* Inside the circle: apply lens pincushion to the local coord, then
       pass to sampleEye. local is already per-eye [0..1]² so just apply
       the distortion around its center. */
    '        vec2 lensed = radialDistort(local, uLensK, uSrcAspect);',
    /* Clamp outside the [0..1] range to black — models the edge of the screen. */
    '        if (lensed.x < 0.0 || lensed.x > 1.0 || lensed.y < 0.0 || lensed.y > 1.0) {',
    '            gl_FragColor = vec4(0.05, 0.05, 0.06, 1.0);',
    '            return;',
    '        }',
    '        gl_FragColor = sampleEye(lensed, eye);',
    '    }',
    '}'
].join('\n');

/* ═══════════════════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════════════════ */
function makeShader(gl, type, src){
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src); gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        var log = gl.getShaderInfoLog(sh);
        gl.deleteShader(sh);
        throw new Error('shader compile: ' + log);
    }
    return sh;
}
function makeProgram(gl){
    var vs = makeShader(gl, gl.VERTEX_SHADER, VERT_SRC);
    var fs = makeShader(gl, gl.FRAGMENT_SHADER, FRAG_SRC);
    var p = gl.createProgram();
    gl.attachShader(p, vs); gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
        throw new Error('program link: ' + gl.getProgramInfoLog(p));
    }
    return p;
}
function fmtTime(s){
    if (!isFinite(s) || s < 0) s = 0;
    var m = Math.floor(s/60), sec = Math.floor(s%60);
    return m + ':' + String(sec).padStart(2,'0');
}
function clamp(v,a,b){return Math.max(a,Math.min(b,v));}

/* Headset lens coefficients — these are pincushion (positive) that the
   pre-distorted video cancels. The output-view shader uses the same
   values inverted (passed as negative into uBarrelK). */
/* Derived from vr-render.js, not re-typed: the pincushion a lens applies is
   exactly the barrel we pre-apply, negated. Two hand-kept copies of the same
   four pairs is how the preview and the exporter drift apart in the first
   place. */
function headsetLens(name){
    /* Read the LENS table, not the negated barrel. Those two stopped being
       negatives of each other on 2026-08-19: the baked pre-distortion is now
       the real inverse of the lens, because negating a radial polynomial only
       inverts it to first order (measured through ffmpeg: 19.15 vs 5.79 mean
       error over the frame). Deriving one from the other by flipping signs
       would put the HMD simulation back on the wrong lens. */
    var L = (window.VRRender && VRRender.HEADSET_LENS) || {};
    var k = L[name] || L.cardboard || { k1: 0.30, k2:-0.05 };
    return { L1: k.k1, L2: k.k2 };
}

/* ═══════════════════════════════════════════════════════════════════════════
   CSS
   ═══════════════════════════════════════════════════════════════════════════ */
var CSS_INJECTED = false;
var CSS = (
    '.vp-wrap{position:relative;border-radius:16px;overflow:hidden;background:#000;user-select:none;-webkit-user-select:none}' +
    '.vp-wrap *{box-sizing:border-box}' +
    '.vp-canvas{display:block;width:100%;height:auto;aspect-ratio:2/1;background:#000}' +
    '.vp-video-hidden{position:absolute;inset:0;width:1px;height:1px;opacity:0;pointer-events:none}' +

    /* Top-right view toggle */
    '.vp-views{position:absolute;top:10px;right:10px;display:flex;gap:4px;background:rgba(0,0,0,.55);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);border-radius:10px;padding:4px;z-index:4;border:1px solid rgba(255,255,255,.08)}' +
    '.vp-view-btn{background:none;border:none;color:rgba(255,255,255,.75);cursor:pointer;font-size:12px;font-weight:600;font-family:system-ui,sans-serif;padding:5px 10px;border-radius:7px;transition:all .15s;display:inline-flex;align-items:center;gap:4px}' +
    '.vp-view-btn:hover{color:#fff;background:rgba(255,255,255,.1)}' +
    '.vp-view-btn.active{color:#fff;background:rgba(56,189,248,.35);box-shadow:inset 0 0 0 1px rgba(56,189,248,.45)}' +
    '.vp-view-btn svg{width:13px;height:13px;fill:currentColor}' +

    /* XR button — pill, green when available */
    '.vp-xr-btn{position:absolute;top:10px;left:10px;display:inline-flex;align-items:center;gap:6px;background:linear-gradient(135deg,#10b981,#059669);color:#fff;border:none;cursor:pointer;font-size:12px;font-weight:700;font-family:system-ui,sans-serif;padding:7px 12px;border-radius:20px;box-shadow:0 2px 10px rgba(16,185,129,.35);transition:transform .15s}' +
    '.vp-xr-btn:hover{transform:scale(1.04)}' +
    '.vp-xr-btn svg{width:14px;height:14px;fill:currentColor}' +
    '.vp-xr-btn[hidden]{display:none!important}' +

    /* Bottom HUD — simple, taller than TimbricaPlayer so volume panel has room */
    '.vp-hud{position:absolute;left:0;right:0;bottom:0;padding:10px 14px;display:flex;align-items:center;gap:10px;background:linear-gradient(transparent,rgba(0,0,0,.7) 50%);z-index:3;transition:opacity .2s}' +
    '.vp-wrap.vp-idle .vp-hud{opacity:.75}' +
    '.vp-wrap:hover .vp-hud,.vp-wrap.vp-paused .vp-hud{opacity:1}' +
    '.vp-btn{background:none;border:none;color:#fff;cursor:pointer;padding:4px;border-radius:4px;display:flex;align-items:center;justify-content:center;transition:background .15s;flex-shrink:0}' +
    '.vp-btn:hover{background:rgba(255,255,255,.15)}' +
    '.vp-btn svg{width:20px;height:20px;fill:currentColor}' +

    '.vp-time{font-size:12px;color:rgba(255,255,255,.9);white-space:nowrap;font-variant-numeric:tabular-nums;font-family:system-ui,sans-serif;flex-shrink:0}' +

    /* Seekbar */
    '.vp-seek{flex:1;position:relative;height:18px;cursor:pointer;display:flex;align-items:center;min-width:40px;touch-action:none}' +
    '.vp-seek-track{position:absolute;left:0;right:0;height:4px;background:rgba(255,255,255,.22);border-radius:2px;top:50%;transform:translateY(-50%)}' +
    '.vp-seek-prog{position:absolute;height:4px;background:#38bdf8;border-radius:2px;top:50%;transform:translateY(-50%);left:0;width:0}' +
    '.vp-seek-head{position:absolute;width:14px;height:14px;background:#fff;border-radius:50%;top:50%;transform:translate(-50%,-50%);box-shadow:0 1px 4px rgba(0,0,0,.4);z-index:3;opacity:0;transition:opacity .15s}' +
    '.vp-seek:hover .vp-seek-head,.vp-seek.dragging .vp-seek-head{opacity:1}' +

    /* Volume — mirrors TimbricaPlayer vertical panel for consistency */
    '.vp-vol{position:relative;flex-shrink:0}' +
    '.vp-vol-panel{position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%) scale(.92);width:36px;height:108px;background:rgba(20,20,24,.92);backdrop-filter:blur(10px) saturate(160%);-webkit-backdrop-filter:blur(10px) saturate(160%);border:1px solid rgba(255,255,255,.08);border-radius:10px;padding:12px 0 10px;display:flex;flex-direction:column;align-items:center;gap:6px;opacity:0;pointer-events:none;transform-origin:50% 100%;transition:opacity .15s,transform .15s;z-index:10;box-shadow:0 4px 16px rgba(0,0,0,.35)}' +
    '.vp-vol:hover .vp-vol-panel,.vp-vol-panel:hover,.vp-vol.vp-vol-open .vp-vol-panel{opacity:1;pointer-events:auto;transform:translateX(-50%) scale(1)}' +
    '.vp-vol-track{position:relative;width:4px;flex:1;background:rgba(255,255,255,.2);border-radius:2px;cursor:pointer;touch-action:none}' +
    '.vp-vol-fill{position:absolute;left:0;right:0;bottom:0;background:#38bdf8;border-radius:2px;pointer-events:none}' +
    '.vp-vol-thumb{position:absolute;left:50%;width:12px;height:12px;background:#fff;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,.4);transform:translate(-50%,50%);pointer-events:none}' +
    '.vp-vol-pct{font-size:12px;color:rgba(255,255,255,.75);font-family:system-ui,sans-serif;font-variant-numeric:tabular-nums;line-height:1;min-height:12px}' +

    /* Center play overlay — big play button when paused */
    '.vp-overlay{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:2;transition:opacity .2s;pointer-events:auto}' +
    '.vp-overlay.vp-hidden{opacity:0;pointer-events:none}' +
    '.vp-overlay-btn{width:64px;height:64px;background:rgba(0,0,0,.55);backdrop-filter:blur(8px);border-radius:50%;display:flex;align-items:center;justify-content:center;transition:transform .15s}' +
    '.vp-overlay-btn:hover{transform:scale(1.08)}' +
    '.vp-overlay-btn svg{width:30px;height:30px;fill:#fff;margin-left:3px}' +

    /* Fullscreen */
    '.vp-wrap.vp-fs{position:fixed!important;inset:0!important;z-index:99999!important;border-radius:0!important}' +
    '.vp-wrap.vp-fs .vp-canvas{width:100%;height:100%;object-fit:contain}' +

    /* Mobile — hide vertical vol panel, XR button gets smaller text */
    '@media(max-width:640px){' +
    '    .vp-hud{padding:7px 10px;gap:6px}' +
    '    .vp-vol-panel{display:none}' +
    '    .vp-view-btn{font-size:12px;padding:4px 7px}' +
    '    .vp-xr-btn{font-size:12px;padding:5px 9px}' +
    '    .vp-overlay-btn{width:52px;height:52px}' +
    '}'
);

function injectCSS(){
    if (CSS_INJECTED) return;
    var s = document.createElement('style');
    s.textContent = CSS;
    document.head.appendChild(s);
    CSS_INJECTED = true;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Icons
   ═══════════════════════════════════════════════════════════════════════════ */
var I = {
    play:  '<svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>',
    pause: '<svg viewBox="0 0 24 24"><path d="M6 4h4v16H6zm8 0h4v16h-4z"/></svg>',
    volOn: '<svg viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0014 8.5v7a4.47 4.47 0 002.5-3.5zM14 3.23v2.06a6.97 6.97 0 010 13.42v2.06A9 9 0 0014 3.23z"/></svg>',
    volOff:'<svg viewBox="0 0 24 24"><path d="M16.5 12A4.5 4.5 0 0014 8.5v2.09l2.4 2.4c.07-.3.1-.6.1-.99zm2.5 0a6.86 6.86 0 01-.54 2.7l1.53 1.53A8.94 8.94 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06A6.97 6.97 0 0119 12zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.9 8.9 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>',
    fsIn:  '<svg viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>',
    fsOut: '<svg viewBox="0 0 24 24"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>',
    outputView: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="9" height="12" rx="1"/><rect x="13" y="6" width="9" height="12" rx="1"/></svg>',
    hmdView:    '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="7" cy="12" r="4"/><circle cx="17" cy="12" r="4"/><path d="M2 8a4 4 0 014-4h12a4 4 0 014 4v8a4 4 0 01-4 4h-2l-2-3h-4l-2 3H6a4 4 0 01-4-4V8z" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>',
    xr:         '<svg viewBox="0 0 24 24"><path d="M2 8a4 4 0 014-4h12a4 4 0 014 4v8a4 4 0 01-4 4h-2l-2-3h-4l-2 3H6a4 4 0 01-4-4V8zm5 6a2 2 0 100-4 2 2 0 000 4zm10 0a2 2 0 100-4 2 2 0 000 4z"/></svg>'
};

/* ═══════════════════════════════════════════════════════════════════════════
   VRPreview class
   ═══════════════════════════════════════════════════════════════════════════ */
function VRPreview(container, opts){
    if (!container) throw new Error('VRPreview: container required');
    injectCSS();
    opts = opts || {};
    var self = this;

    /* Settings defaults mirror the blade's defaults so initial paint matches
       what the FFmpeg pipeline would actually produce. */
    var settings = {
        mode: 'sbs',          /* sbs | parallax | passthrough | sphere */
        parallax: 40,         /* 10..60 — converted to fraction-of-width downstream */
        headset: 'cardboard',
        ipd: 63,
        barrel: 'medium',     /* off | light | medium | strong */
        resolution: 'source', /* source | 1080p | 1440p */
        metadata: 'sbs',
        coverage: 180         /* sphere mode: horizontal arc in degrees (120/180/240) */
    };
    var view = 'output';      /* 'output' | 'hmd' */
    var isFS = false;
    var destroyed = false;
    var currentVolume = 1;
    var rafHandle = 0;
    var rvfcHandle = 0;
    var useRVFC = false;
    var xrSession = null;
    var labels = Object.assign({
        output: 'VR output',
        hmd: 'As in headset',
        xr: 'Open in VR',
        volume: 'Volume',
        mute: 'Mute',
        play: 'Play',
        pause: 'Pause',
        fullscreen: 'Fullscreen'
    }, opts.labels || {});

    /* ═══ DOM ═══ */
    var wrap = document.createElement('div');
    wrap.className = 'vp-wrap vp-paused vp-idle';

    var canvas = document.createElement('canvas');
    canvas.className = 'vp-canvas';
    canvas.width = 1920; canvas.height = 960;
    wrap.appendChild(canvas);

    var video = document.createElement('video');
    video.className = 'vp-video-hidden';
    video.playsInline = true;
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    wrap.appendChild(video);
    self.video = video;

    /* Center play overlay */
    var overlay = document.createElement('div');
    overlay.className = 'vp-overlay';
    overlay.innerHTML = '<div class="vp-overlay-btn">' + I.play + '</div>';
    wrap.appendChild(overlay);

    /* View toggle */
    var views = document.createElement('div');
    views.className = 'vp-views';
    var btnOutput = document.createElement('button');
    btnOutput.className = 'vp-view-btn active';
    btnOutput.innerHTML = I.outputView + '<span>' + labels.output + '</span>';
    btnOutput.setAttribute('aria-pressed', 'true');
    var btnHmd = document.createElement('button');
    btnHmd.className = 'vp-view-btn';
    btnHmd.innerHTML = I.hmdView + '<span>' + labels.hmd + '</span>';
    btnHmd.setAttribute('aria-pressed', 'false');
    views.appendChild(btnOutput);
    views.appendChild(btnHmd);
    wrap.appendChild(views);

    /* XR button (hidden until isSessionSupported resolves true) */
    var xrBtn = document.createElement('button');
    xrBtn.className = 'vp-xr-btn';
    xrBtn.hidden = true;
    xrBtn.innerHTML = I.xr + '<span>' + labels.xr + '</span>';
    wrap.appendChild(xrBtn);

    /* HUD — play / time / seek / volume / fullscreen */
    var hud = document.createElement('div');
    hud.className = 'vp-hud';

    var playBtn = document.createElement('button');
    playBtn.className = 'vp-btn';
    playBtn.innerHTML = I.play;
    playBtn.setAttribute('aria-label', labels.play);
    hud.appendChild(playBtn);

    var timeEl = document.createElement('span');
    timeEl.className = 'vp-time';
    timeEl.textContent = '0:00 / 0:00';
    hud.appendChild(timeEl);

    var seek = document.createElement('div');
    seek.className = 'vp-seek';
    var seekTrack = document.createElement('div'); seekTrack.className = 'vp-seek-track';
    var seekProg = document.createElement('div'); seekProg.className = 'vp-seek-prog';
    var seekHead = document.createElement('div'); seekHead.className = 'vp-seek-head';
    seek.appendChild(seekTrack); seek.appendChild(seekProg); seek.appendChild(seekHead);
    hud.appendChild(seek);

    /* Volume — vertical panel (matches TimbricaPlayer) */
    var volWrap = document.createElement('div'); volWrap.className = 'vp-vol';
    var volBtn = document.createElement('button');
    volBtn.className = 'vp-btn';
    volBtn.innerHTML = I.volOn;
    volBtn.setAttribute('aria-label', labels.mute);
    var volPanel = document.createElement('div'); volPanel.className = 'vp-vol-panel';
    var volTrack = document.createElement('div'); volTrack.className = 'vp-vol-track';
    volTrack.setAttribute('role', 'slider');
    volTrack.setAttribute('aria-orientation', 'vertical');
    volTrack.setAttribute('aria-label', labels.volume);
    volTrack.setAttribute('aria-valuemin', '0');
    volTrack.setAttribute('aria-valuemax', '100');
    volTrack.setAttribute('aria-valuenow', '100');
    volTrack.tabIndex = 0;
    var volFill = document.createElement('div'); volFill.className = 'vp-vol-fill'; volFill.style.height = '100%';
    var volThumb = document.createElement('div'); volThumb.className = 'vp-vol-thumb'; volThumb.style.bottom = '100%';
    volTrack.appendChild(volFill); volTrack.appendChild(volThumb);
    var volPct = document.createElement('span'); volPct.className = 'vp-vol-pct'; volPct.textContent = '100';
    volPanel.appendChild(volTrack); volPanel.appendChild(volPct);
    volWrap.appendChild(volBtn); volWrap.appendChild(volPanel);
    hud.appendChild(volWrap);

    var fsBtn = document.createElement('button');
    fsBtn.className = 'vp-btn';
    fsBtn.innerHTML = I.fsIn;
    fsBtn.setAttribute('aria-label', labels.fullscreen);
    hud.appendChild(fsBtn);

    wrap.appendChild(hud);

    container.innerHTML = '';
    container.appendChild(wrap);

    /* ═══ WebGL init ═══ */
    var glOpts = { alpha: false, antialias: true, depth: false, stencil: false, preserveDrawingBuffer: false };
    var gl = canvas.getContext('webgl', glOpts) || canvas.getContext('experimental-webgl', glOpts);
    if (!gl) {
        /* Headless/old Android WebView/locked-down browser (Huawei LMR-LX9
           Yandex 26 was the first Sentry sighting — TIMBRICA-FRONTEND-62).
           The original code `return`ed here without assigning the public API,
           so callers crashed with "player.load is not a function" the next
           line after `new VRPreview(...)`. Now: degrade to a fully no-op
           instance with `self.error` set, dispatch `vrpreview:error` for
           callers who want to react via the existing event-based contract
           (matches `vrpreview:ready`), and let `new VRPreview(...)` complete
           normally so caller-side null-checks remain optional. */
        wrap.innerHTML = '<div style="padding:40px;text-align:center;color:#fff;">' + ((window.__commonStrings||{}).js_webgl_unsupported || "WebGL not supported") + '</div>';
        self.error = 'webgl_not_supported';
        self.isXRSupported = Promise.resolve(false);
        var noop = function(){};
        self.load = noop;
        self.setSettings = noop;
        self.setView = noop;
        self.play = function(){ return Promise.resolve(); };
        self.pause = noop;
        self.seek = noop;
        self.setVolume = noop;
        self.enterXR = function(){ return Promise.reject(new Error('webgl_not_supported')); };
        self.destroy = function(){ wrap.remove(); };
        Promise.resolve().then(function(){
            container.dispatchEvent(new CustomEvent('vrpreview:error', { detail: { reason: 'webgl_not_supported' } }));
        });
        return;
    }

    var program = makeProgram(gl);
    gl.useProgram(program);

    /* Fullscreen quad */
    var quadBuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
    var aPos = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    /* Video texture */
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    /* Upload a 1x1 black placeholder so the shader can sample before the first frame. */
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, 1, 1, 0, gl.RGB, gl.UNSIGNED_BYTE, new Uint8Array([0,0,0]));

    /* Uniforms */
    var U = {
        tex: gl.getUniformLocation(program, 'uTex'),
        mode: gl.getUniformLocation(program, 'uMode'),
        view: gl.getUniformLocation(program, 'uView'),
        parallaxFrac: gl.getUniformLocation(program, 'uParallaxFrac'),
        barrelK: gl.getUniformLocation(program, 'uBarrelK'),
        lensK: gl.getUniformLocation(program, 'uLensK'),
        srcAspect: gl.getUniformLocation(program, 'uSrcAspect'),
        outAspect: gl.getUniformLocation(program, 'uOutAspect'),
        canvasAspect: gl.getUniformLocation(program, 'uCanvasAspect'),
        padScale: gl.getUniformLocation(program, 'uPadScale'),
        ipdFrac: gl.getUniformLocation(program, 'uIpdFrac'),
        eyeRadius: gl.getUniformLocation(program, 'uEyeRadius'),
        sphere: gl.getUniformLocation(program, 'uSphere'),
        sphereHalfW: gl.getUniformLocation(program, 'uSphereHalfW'),
        sphereHalfH: gl.getUniformLocation(program, 'uSphereHalfH')
    };
    gl.uniform1i(U.tex, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);

    /* ═══ Render loop ═══ */
    var srcW = 1, srcH = 1, lastFrame = -1;

    function updateUniforms(){
        /* Mode int — 0 sbs, 1 parallax, 2 passthrough */
        var modeInt = settings.mode === 'parallax' ? 1 : (settings.mode === 'passthrough' ? 2 : 0);
        gl.uniform1i(U.mode, modeInt);
        gl.uniform1i(U.view, view === 'hmd' ? 1 : 0);

        /* Sphere (curved 360) — compute the video band geometry to MATCH the
           FFmpeg scale+pad math so the preview is WYSIWYG. The video is fit
           (aspect-preserved) into a box of width = coverage/360 of the equirect
           canvas and full canvas height, then centered. */
        var isSphere = settings.mode === 'sphere';
        gl.uniform1i(U.sphere, isSphere ? 1 : 0);
        if (isSphere) {
            var covFrac = (settings.coverage || 180) / 360;   /* band width as fraction of 2:1 canvas */
            var boxW = covFrac;                                /* box width (fraction of canvas width) */
            var boxH = 1.0;                                    /* box height = full canvas height */
            /* canvas is 2:1, so 1 unit of width = 0.5 unit of height in aspect.
               Fit src aspect into (boxW*W) x (boxH*H) preserving aspect. */
            var srcAR = srcW / Math.max(1, srcH);
            var canvasAR = 2.0;                                /* equirect 2:1 */
            /* box aspect in canvas pixels: (boxW*2) : (boxH*1) */
            var boxAR = (boxW * canvasAR) / boxH;
            var halfW, halfH;
            if (srcAR >= boxAR) {                              /* width-limited */
                halfW = boxW / 2;
                halfH = (boxW * canvasAR / srcAR) / 2;
            } else {                                           /* height-limited */
                halfH = boxH / 2;
                halfW = (boxH * srcAR / canvasAR) / 2;
            }
            gl.uniform1f(U.sphereHalfW, halfW);
            gl.uniform1f(U.sphereHalfH, halfH);
        }

        /* Parallax fraction — JS slider 10→1%, 40→4%, 60→6% */
        var pFrac = (settings.parallax / 1000);
        if (settings.headset === 'custom') pFrac *= (settings.ipd / 63);
        gl.uniform1f(U.parallaxFrac, pFrac);

        /* Barrel pre-distortion — the SAME function the exporter calls, so a
           change to the coefficients can never reach one lane and not the other. */
        var lens = headsetLens(settings.headset);
        var bk = (window.VRRender && VRRender.barrelK) ? VRRender.barrelK(settings) : null;
        gl.uniform2f(U.barrelK, bk ? bk.k1 : 0, bk ? bk.k2 : 0);

        /* Lens pincushion for HMD view — always full strength (that's what real lens does) */
        gl.uniform2f(U.lensK, lens.L1, lens.L2);

        gl.uniform1f(U.srcAspect, srcW / Math.max(1, srcH));
        gl.uniform1f(U.outAspect, (srcW * 2) / Math.max(1, srcH));

        var cAspect = canvas.width / Math.max(1, canvas.height);
        gl.uniform1f(U.canvasAspect, cAspect);

        /* No letterbox any more. The resolution picker is PER EYE now, so the
           output keeps the source aspect and there are no bars to preview —
           the old picker fitted the 32:9 SBS frame into a 16:9 box and the
           preview faithfully reproduced half a screen of black. */
        gl.uniform1f(U.padScale, 1.0);

        /* HMD viewport geometry — cardboard has IPD ~63mm, eyes ~60-65mm apart in the case.
           uIpdFrac is user IPD relative to 63mm. eyeRadius is half-width of the cardboard
           case slot — about 0.4 of a half-canvas. */
        gl.uniform1f(U.ipdFrac, settings.ipd / 63);
        gl.uniform1f(U.eyeRadius, 0.42);
    }

    /* An HLG/PQ clip uploads as its raw coded signal: the HDR → SDR door
       (hdr-to-sdr.js, docs/hdr-to-sdr.md) hands the SDR picture of the same
       size, or the element itself when the clip is SDR — the export does the
       same in vr-render.js, so the preview is the file. */
    function sdrVideo(){
        return window.HdrToSdr ? window.HdrToSdr.image(video, { texture: true }) : video;
    }

    function uploadFrame(){
        if (!srcW || !srcH) return;
        /* A <video> that has dimensions but no decoded frame yet makes
           texImage2D log `INVALID_VALUE: no video` — not an exception, so the
           try/catch below never saw it, and the console noise then looks like
           whatever change happens to be in flight. HAVE_CURRENT_DATA = 2. */
        if (video.readyState < 2) return;
        try {
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, sdrVideo());
        } catch(e) { /* CORS/draw-null frame — ignore one frame */ }
    }

    function resizeCanvas(){
        var rect = canvas.getBoundingClientRect();
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var w = Math.max(64, Math.round(rect.width * dpr));
        var h = Math.max(32, Math.round(w / 2));  /* SBS 2:1 */
        if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w; canvas.height = h;
            gl.viewport(0, 0, w, h);
        }
    }

    function render(){
        if (destroyed) return;
        resizeCanvas();
        updateUniforms();
        uploadFrame();
        gl.clearColor(0, 0, 0, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    /* Drive render loop: prefer requestVideoFrameCallback so we only redraw
       when the video actually advances; fall back to RAF. */
    function startLoop(){
        stopLoop();
        if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
            useRVFC = true;
            var rvfc = function(){
                if (destroyed) return;
                render();
                rvfcHandle = video.requestVideoFrameCallback(rvfc);
            };
            rvfcHandle = video.requestVideoFrameCallback(rvfc);
        } else {
            var raf = function(){
                if (destroyed) return;
                if (!video.paused && video.currentTime !== lastFrame) {
                    lastFrame = video.currentTime;
                    render();
                }
                rafHandle = requestAnimationFrame(raf);
            };
            rafHandle = requestAnimationFrame(raf);
        }
    }
    function stopLoop(){
        if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
        if (rvfcHandle && video.cancelVideoFrameCallback) {
            try { video.cancelVideoFrameCallback(rvfcHandle); } catch(e){}
            rvfcHandle = 0;
        }
    }

    /* Render whenever settings change — paused video still needs to repaint. */
    function renderOnce(){ render(); }

    /* ═══ Events ═══ */

    function togglePlay(){ if (video.paused) video.play().catch(function(){}); else video.pause(); }
    playBtn.addEventListener('click', togglePlay);
    overlay.addEventListener('click', togglePlay);
    canvas.addEventListener('click', togglePlay);

    /* Load-settle guard: the live preview depends on the BROWSER being able to
       decode the source. For formats browsers can't play (rawvideo / MJPEG /
       MS-Video1 / Cinepak AVI, WMV, often HEVC) `loadedmetadata` never fires —
       historically a silent dead-end. We now settle exactly once via one of:
       loadedmetadata (ok) → 'vrpreview:ready'; the <video> 'error' event or a
       watchdog timeout → 'vrpreview:loaderror'. The host tool treats loaderror
       as "no live preview" and falls back to the FFmpeg probe + convert path
       (FFmpeg decodes far more than any <video>). The live preview is therefore
       best-effort, never a gate. */
    var loadSettled = false, loadWatchdog = 0;
    function clearLoadWatchdog(){ if (loadWatchdog){ clearTimeout(loadWatchdog); loadWatchdog = 0; } }
    function settleLoadError(reason){
        if (loadSettled || destroyed) return;
        loadSettled = true; clearLoadWatchdog();
        container.dispatchEvent(new CustomEvent('vrpreview:loaderror', {
            detail: { reason: reason, code: (video.error && video.error.code) || 0 }
        }));
    }
    video.addEventListener('loadedmetadata', function(){
        /* ⚠️ `loadedmetadata` firing is NOT proof of a picture. Chromium (and
           Firefox) accept an HEVC mp4/mov far enough to report readyState 4,
           a correct duration and NO error — while videoWidth/videoHeight stay
           0. Every iPhone shooting in "High Efficiency" produces exactly that
           file, and it is the single most likely input /video-to-vr gets.
           The old `|| 1` turned "no picture" into a 1x1 source: the host tool
           believed the preview, never asked the mp4box probe (which reads the
           real 1920x1080 in ~400 ms), and produced a 4x2 video it called a
           success. A settle with no dimensions is a LOAD ERROR — that path
           already exists, shows an honest placeholder and runs the probe. */
        if (!(video.videoWidth >= 16 && video.videoHeight >= 16)) {
            settleLoadError('no_dimensions');
            return;
        }
        loadSettled = true; clearLoadWatchdog();
        srcW = video.videoWidth;
        srcH = video.videoHeight;
        timeEl.textContent = '0:00 / ' + fmtTime(video.duration);
        wrap.classList.remove('vp-idle');
        container.dispatchEvent(new CustomEvent('vrpreview:ready', {
            detail: { video: video, w: srcW, h: srcH, duration: video.duration }
        }));
        /* preload="metadata" gives us dimensions/duration but no decoded
           pixels yet, so the first texImage2D upload paints pure black. Nudge
           currentTime by a hair to force a decode — the subsequent 'seeked'
           event means a frame is ready to upload. The nudge is well below
           any common frame duration (1ms at 60fps = 0.06 frames) so we still
           end up on frame 0 visually. */
        if (video.readyState < 2) {
            try { video.currentTime = 0.001; } catch(_){}
        } else {
            renderOnce();
        }
    });

    /* Any of these means "the current frame has pixels ready to paint".
       Different browsers fire different subsets on first-load — Chrome
       usually fires loadeddata first, Safari sometimes only fires seeked
       after the nudge above. Register all three and render; repeat paints
       are cheap. */
    video.addEventListener('loadeddata', renderOnce);
    video.addEventListener('seeked', renderOnce);
    video.addEventListener('canplay', renderOnce);
    /* Browser couldn't decode the source → settle as loaderror (not silent). */
    video.addEventListener('error', function(){ settleLoadError('decode'); });
    /* Also request a one-shot video-frame callback so we catch the very
       first decoded frame on browsers that support it — more reliable than
       waiting for the coarse-grained events above. */
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
        video.requestVideoFrameCallback(function(){ renderOnce(); });
    }
    video.addEventListener('play', function(){
        wrap.classList.remove('vp-paused');
        overlay.classList.add('vp-hidden');
        playBtn.innerHTML = I.pause;
        startLoop();
        container.dispatchEvent(new CustomEvent('vrpreview:play'));
    });
    video.addEventListener('pause', function(){
        wrap.classList.add('vp-paused');
        overlay.classList.remove('vp-hidden');
        playBtn.innerHTML = I.play;
        /* Keep one final paint so the last frame shows correctly. */
        render();
        container.dispatchEvent(new CustomEvent('vrpreview:pause'));
    });
    video.addEventListener('ended', function(){
        container.dispatchEvent(new CustomEvent('vrpreview:ended'));
    });
    video.addEventListener('timeupdate', function(){
        if (!isDraggingSeek) {
            var pct = video.duration ? (video.currentTime / video.duration * 100) : 0;
            seekProg.style.width = pct + '%';
            seekHead.style.left = pct + '%';
            timeEl.textContent = fmtTime(video.currentTime) + ' / ' + fmtTime(video.duration);
        }
        container.dispatchEvent(new CustomEvent('vrpreview:timeupdate', { detail: { t: video.currentTime } }));
    });
    video.addEventListener('volumechange', paintVolume);

    /* Seek */
    var isDraggingSeek = false;
    function seekFromEvent(e){
        if (!video.duration) return;
        var r = seek.getBoundingClientRect();
        var cx = e.touches ? e.touches[0].clientX : e.clientX;
        var pct = clamp((cx - r.left) / r.width, 0, 1);
        video.currentTime = pct * video.duration;
        seekProg.style.width = (pct * 100) + '%';
        seekHead.style.left = (pct * 100) + '%';
        timeEl.textContent = fmtTime(video.currentTime) + ' / ' + fmtTime(video.duration);
        render();
    }
    seek.addEventListener('mousedown', function(e){
        e.preventDefault();
        isDraggingSeek = true;
        seek.classList.add('dragging');
        seekFromEvent(e);
        document.addEventListener('mousemove', seekMove);
        document.addEventListener('mouseup', seekEnd);
    });
    seek.addEventListener('touchstart', function(e){
        e.preventDefault();
        isDraggingSeek = true;
        seek.classList.add('dragging');
        seekFromEvent(e);
        document.addEventListener('touchmove', seekMove, {passive:false});
        document.addEventListener('touchend', seekEnd);
    }, {passive:false});
    function seekMove(e){ if (isDraggingSeek) { e.preventDefault && e.preventDefault(); seekFromEvent(e); } }
    function seekEnd(){
        isDraggingSeek = false;
        seek.classList.remove('dragging');
        document.removeEventListener('mousemove', seekMove);
        document.removeEventListener('mouseup', seekEnd);
        document.removeEventListener('touchmove', seekMove);
        document.removeEventListener('touchend', seekEnd);
    }

    /* Volume */
    function paintVolume(){
        var v = video.muted ? 0 : video.volume;
        var pct = Math.round(v * 100);
        volFill.style.height = pct + '%';
        volThumb.style.bottom = pct + '%';
        volPct.textContent = pct;
        volTrack.setAttribute('aria-valuenow', String(pct));
        volBtn.innerHTML = (video.muted || v === 0) ? I.volOff : I.volOn;
    }
    function setVolFromEvent(e){
        var r = volTrack.getBoundingClientRect();
        var cy = e.touches ? e.touches[0].clientY : e.clientY;
        var v = clamp(1 - (cy - r.top) / r.height, 0, 1);
        video.volume = v;
        if (v > 0) { video.muted = false; currentVolume = v; }
        paintVolume();
    }
    var isDraggingVol = false;
    var volStart = function(e){
        e.preventDefault();
        isDraggingVol = true;
        volTrack.classList.add('dragging');
        setVolFromEvent(e);
        document.addEventListener('mousemove', volMove);
        document.addEventListener('mouseup', volEnd);
        document.addEventListener('touchmove', volMove, {passive:false});
        document.addEventListener('touchend', volEnd);
    };
    var volMove = function(e){ if (isDraggingVol) { e.preventDefault && e.preventDefault(); setVolFromEvent(e); } };
    var volEnd = function(){
        isDraggingVol = false;
        volTrack.classList.remove('dragging');
        document.removeEventListener('mousemove', volMove);
        document.removeEventListener('mouseup', volEnd);
        document.removeEventListener('touchmove', volMove);
        document.removeEventListener('touchend', volEnd);
    };
    volTrack.addEventListener('mousedown', volStart);
    volTrack.addEventListener('touchstart', volStart, {passive:false});
    volWrap.addEventListener('wheel', function(e){
        e.preventDefault();
        var d = e.deltaY > 0 ? -0.05 : 0.05;
        video.volume = clamp(video.volume + d, 0, 1);
        if (video.volume > 0) { video.muted = false; currentVolume = video.volume; }
        paintVolume();
    }, {passive:false});
    volBtn.addEventListener('click', function(e){
        e.stopPropagation();
        if (video.muted || video.volume === 0) {
            video.muted = false;
            if (video.volume === 0) video.volume = currentVolume || 0.5;
        } else {
            video.muted = true;
        }
        paintVolume();
    });

    /* View toggle */
    btnOutput.addEventListener('click', function(){ self.setView('output'); });
    btnHmd.addEventListener('click', function(){ self.setView('hmd'); });

    /* Fullscreen — three-tier graceful chain (see video-player.js for full
       rationale): wrap native FS → CSS pseudo-FS. We try wrap (not <video>)
       because vr-preview renders to a WebGL <canvas>, so the player UI must
       go fullscreen, not the hidden source <video>. Every native call is
       .catch()ed so a denial never escapes as unhandled rejection. */
    var cssEscHandler = null;
    function tryNativeFs(el){
        try {
            if (el.requestFullscreen) {
                var p = el.requestFullscreen();
                return (p && typeof p.then === 'function') ? p : Promise.resolve();
            }
            if (el.webkitRequestFullscreen) { el.webkitRequestFullscreen(); return Promise.resolve(); }
        } catch (e) { return Promise.reject(e); }
        return null;
    }
    function enterCssFs(){
        wrap.classList.add('vp-fs');
        isFS = true;
        fsBtn.innerHTML = I.fsOut;
        cssEscHandler = function(e){ if (e.key === 'Escape') exitCssFs(); };
        document.addEventListener('keydown', cssEscHandler, true);
    }
    function exitCssFs(){
        wrap.classList.remove('vp-fs');
        isFS = false;
        fsBtn.innerHTML = I.fsIn;
        if (cssEscHandler) {
            document.removeEventListener('keydown', cssEscHandler, true);
            cssEscHandler = null;
        }
    }
    fsBtn.addEventListener('click', function(){
        if (!isFS) {
            var p = tryNativeFs(wrap);
            if (p) p.then(null, enterCssFs); else enterCssFs();
        } else {
            if (cssEscHandler) { exitCssFs(); return; }
            try {
                if (document.exitFullscreen) {
                    var p2 = document.exitFullscreen();
                    if (p2 && typeof p2.then === 'function') p2.then(null, function(){});
                    return;
                }
                if (document.webkitExitFullscreen) { document.webkitExitFullscreen(); return; }
            } catch (e) { /* fall through */ }
            isFS = false; wrap.classList.remove('vp-fs'); fsBtn.innerHTML = I.fsIn;
        }
    });
    function onFsChange(){
        if (cssEscHandler) return;
        isFS = !!(document.fullscreenElement || document.webkitFullscreenElement);
        wrap.classList.toggle('vp-fs', isFS);
        fsBtn.innerHTML = isFS ? I.fsOut : I.fsIn;
    }
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);

    /* ═══ WebXR ═══
       Probe support, reveal button if yes. Click attempts immersive-vr.
       The session renders the source-video texture via the same shader, one
       XRView per eye. Parallax/barrel settings apply so the user previews
       with their real lens settings. */
    self.isXRSupported = (function(){
        if (!navigator.xr || !navigator.xr.isSessionSupported) return Promise.resolve(false);
        return navigator.xr.isSessionSupported('immersive-vr').catch(function(){ return false; });
    })();
    self.isXRSupported.then(function(ok){
        if (ok && !destroyed) xrBtn.hidden = false;
    });

    var xrGl = null, xrRefSpace = null, xrFrameHandle = 0;
    function onXRFrame(t, frame){
        if (!xrSession || destroyed) return;
        var pose = frame.getViewerPose(xrRefSpace);
        var layer = xrSession.renderState.baseLayer;
        xrGl.bindFramebuffer(xrGl.FRAMEBUFFER, layer.framebuffer);
        xrGl.viewport(0, 0, layer.framebufferWidth, layer.framebufferHeight);
        xrGl.clearColor(0, 0, 0, 1);
        xrGl.clear(xrGl.COLOR_BUFFER_BIT);
        /* Upload fresh frame */
        try { xrGl.bindTexture(xrGl.TEXTURE_2D, tex); xrGl.texImage2D(xrGl.TEXTURE_2D, 0, xrGl.RGB, xrGl.RGB, xrGl.UNSIGNED_BYTE, sdrVideo()); } catch(e){}
        if (pose) {
            for (var i = 0; i < pose.views.length; i++) {
                var v = pose.views[i];
                var vp = layer.getViewport(v);
                xrGl.viewport(vp.x, vp.y, vp.width, vp.height);
                /* Each eye: sample the appropriate half of the SBS texture.
                   We set uMode=2 (passthrough — source-is-already-SBS) because
                   the texture is already the SBS output; no further split. But
                   we have to FIRST render output to an offscreen FBO. For v1
                   simplicity, we pass the raw source and use mode directly; this
                   applies parallax/barrel per-eye correctly. */
                updateUniforms();
                /* Override view to output and bias mode so each eye gets its half. */
                xrGl.uniform1i(U.view, 0);
                xrGl.drawArrays(xrGl.TRIANGLES, 0, 6);
            }
        }
        xrFrameHandle = xrSession.requestAnimationFrame(onXRFrame);
    }

    self.enterXR = function(){
        if (xrSession || !navigator.xr) return;
        navigator.xr.requestSession('immersive-vr').then(function(session){
            xrSession = session;
            /* Reuse the same WebGL context so textures/programs stay hot. */
            xrGl = gl;
            return xrGl.makeXRCompatible ? xrGl.makeXRCompatible() : Promise.resolve();
        }).then(function(){
            if (!xrSession) return;
            /* eslint-disable-next-line no-undef */
            var layer = new XRWebGLLayer(xrSession, xrGl);
            xrSession.updateRenderState({ baseLayer: layer });
            xrSession.requestReferenceSpace('local').then(function(ref){
                xrRefSpace = ref;
                if (video.paused) video.play().catch(function(){});
                xrFrameHandle = xrSession.requestAnimationFrame(onXRFrame);
                container.dispatchEvent(new CustomEvent('vrpreview:xrstart'));
            });
            xrSession.addEventListener('end', function(){
                xrSession = null; xrRefSpace = null; xrFrameHandle = 0;
                container.dispatchEvent(new CustomEvent('vrpreview:xrend'));
            });
        }).catch(function(err){
            container.dispatchEvent(new CustomEvent('vrpreview:xrerror', { detail: { error: err } }));
        });
    };
    /* Завершение сеанса — ИДЕМПОТЕНТНО и асинхронно.
     *
     * `XRSession.end()` возвращает промис. В destroy() он был обёрнут
     * СИНХРОННЫМ try/catch — такая обёртка отказ промиса поймать не может в
     * принципе, и он улетал в unhandledrejection: «Failed to execute 'end' on
     * 'XRSession': XRSession has already ended» (Sentry FS, /video-to-vr —
     * человек вышел из VR и нажал «Новое видео»).
     *
     * Ссылка обнуляется ДО вызова: браузер закрывает сеанс не мгновенно, а
     * событие 'end' приходит позже, так что без этого второй вызов успевал
     * прийти на тот же уже закрывающийся сеанс. */
    function endXR(){
        var s = xrSession;
        xrSession = null;
        if (!s) return;
        try {
            var p = s.end();
            if (p && typeof p.catch === 'function') p.catch(function(){});
        } catch (e) {}
    }
    self.exitXR = endXR;
    xrBtn.addEventListener('click', function(){ self.enterXR(); });

    /* Window resize → repaint. Safari до 13.1 наблюдателя не знает: голый
       конструктор ронял new VRPreview(...), и /video-to-vr оставался без
       предпросмотра. Без него перерисовка — по окну (во время игры кадр и так
       перемеряет холст на каждой отрисовке). */
    var resizeObs = null;
    if (typeof ResizeObserver === 'function') {
        resizeObs = new ResizeObserver(function(){ renderOnce(); });
        resizeObs.observe(canvas);
    } else {
        window.addEventListener('resize', renderOnce);
    }

    /* ═══ Public API ═══ */

    self.load = function(srcOrFile){
        loadSettled = false; clearLoadWatchdog();
        if (typeof srcOrFile === 'string') {
            video.src = srcOrFile;
        } else if (srcOrFile instanceof File || srcOrFile instanceof Blob) {
            if (video._blobUrl) URL.revokeObjectURL(video._blobUrl);
            var u = URL.createObjectURL(srcOrFile);
            video.src = u;
            video._blobUrl = u;
        }
        video.load();
        /* Backstop: some browsers neither fire loadedmetadata NOR error for an
           undecodable source — they just stall. Blob metadata normally settles
           in well under a second, so a 12 s ceiling can't false-trip a valid
           file while still rescuing the silent-stall case. */
        loadWatchdog = setTimeout(function(){ settleLoadError('timeout'); }, 12000);
    };

    self.setSettings = function(partial){
        Object.assign(settings, partial || {});
        /* Sphere mode is a single mono 360 frame — the HMD (cardboard-lens
           simulation) and the SBS-per-eye WebXR path don't apply. Hide those
           controls and force the output view so the preview stays honest. */
        var isSphere = settings.mode === 'sphere';
        if (views) views.style.display = isSphere ? 'none' : '';
        if (isSphere) {
            xrBtn.hidden = true;
            if (view !== 'output') self.setView('output');
        } else if (!destroyed) {
            /* Restore XR button if the platform supports it */
            self.isXRSupported.then(function(ok){ if (ok && !destroyed && settings.mode !== 'sphere') xrBtn.hidden = false; });
        }
        renderOnce();
    };

    self.setView = function(v){
        view = v === 'hmd' ? 'hmd' : 'output';
        btnOutput.classList.toggle('active', view === 'output');
        btnHmd.classList.toggle('active', view === 'hmd');
        btnOutput.setAttribute('aria-pressed', String(view === 'output'));
        btnHmd.setAttribute('aria-pressed', String(view === 'hmd'));
        renderOnce();
    };

    self.play = function(){ return video.play(); };
    self.pause = function(){ video.pause(); };
    self.seek = function(t){ video.currentTime = clamp(t, 0, video.duration || Infinity); };
    self.setVolume = function(v){
        video.volume = clamp(v, 0, 1);
        video.muted = v <= 0;
        if (v > 0) currentVolume = v;
    };

    Object.defineProperty(self, 'currentTime', { get: function(){ return video.currentTime; } });
    Object.defineProperty(self, 'duration', { get: function(){ return video.duration; } });
    Object.defineProperty(self, 'paused', { get: function(){ return video.paused; } });
    Object.defineProperty(self, 'view', { get: function(){ return view; } });
    Object.defineProperty(self, 'canvas', { get: function(){ return canvas; } });
    Object.defineProperty(self, 'container', { get: function(){ return wrap; } });

    self.destroy = function(){
        destroyed = true;
        stopLoop();
        try { video.pause(); } catch(e){}
        if (video._blobUrl) URL.revokeObjectURL(video._blobUrl);
        video.removeAttribute('src'); video.load();
        document.removeEventListener('fullscreenchange', onFsChange);
        document.removeEventListener('webkitfullscreenchange', onFsChange);
        if (cssEscHandler) { document.removeEventListener('keydown', cssEscHandler, true); cssEscHandler = null; }
        if (resizeObs) resizeObs.disconnect();
        else window.removeEventListener('resize', renderOnce);
        endXR();
        /* GL cleanup */
        try { gl.deleteProgram(program); gl.deleteBuffer(quadBuf); gl.deleteTexture(tex); } catch(e){}
        wrap.remove();
    };

    /* Initial paint */
    renderOnce();
}

window.VRPreview = VRPreview;

})();
